import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

// Real sticker artworks (512px transparent PNGs).
const STICKERS = [
  { key: 'bordeaux', src: '/stickers/tsanga-sticker-bordeaux-512.png',      label: 'Bordeaux' },
  { key: 'bleu',     src: '/stickers/tsanga-sticker-bleunuit-512.png',      label: 'Bleu Nuit' },
  { key: 'argent',   src: '/stickers/tsanga-sticker-argent-512.png',        label: 'Argent' },
  { key: 'holo',     src: '/stickers/tsanga-sticker-holographique-512.png', label: 'Holo' },
];

// "/shop-tee-front.png" → "/shop-tee-front-400.webp 400w, /shop-tee-front-800.webp 800w"
// "/stickers/…-argent-512.png" → "/stickers/…-argent-128.webp 128w, …-256.webp 256w"
const webpSrcSet = (src, widths) => {
  const stem = src.replace(/(-\d+)?\.png$/, '');
  return widths.map(w => `${stem}-${w}.webp ${w}w`).join(', ');
};

// Sert le WebP quand le navigateur sait le lire, le PNG sinon. Un <source> dont
// le type est inconnu est ignoré en silence, donc le PNG reste le filet sans
// qu'on ait à renifler l'agent.
function Picture({ src, widths, sizes, alt, wrapperStyle, style, ...imgProps }) {
  return (
    <picture style={wrapperStyle}>
      <source type="image/webp" sizes={sizes} srcSet={webpSrcSet(src, widths)} />
      <img src={src} alt={alt} loading="lazy" decoding="async" style={style} {...imgProps} />
    </picture>
  );
}

function VisualSlot({ tag }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 14,
      opacity: 0.55,
    }} aria-hidden="true">
      <span style={{
        display: 'inline-block', width: 38, height: 38, borderRadius: '50%',
        border: '1px dashed rgba(255,255,255,0.35)',
      }} />
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.28em',
        color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
      }}>
        {tag} · visual soon
      </span>
    </div>
  );
}

function ProductVisual({ front, back, alt }) {
  const [showBack, setShowBack] = useState(false);
  return (
    <motion.div
      onClick={() => setShowBack(v => !v)}
      onHoverStart={() => setShowBack(true)}
      onHoverEnd={() => setShowBack(false)}
      role="button"
      tabIndex={0}
      aria-label={`${alt} — click to flip`}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowBack(v => !v)}
      style={{ width: '100%', height: '100%', position: 'relative', cursor: 'none', overflow: 'hidden' }}
    >
      {/* Le <picture> entier est la cible d'AnimatePresence : si le <source> vivait
          en dehors, il basculerait dès le clic et l'image sortante afficherait déjà
          le verso — le fondu se jouerait alors entre deux fois la même image. */}
      <AnimatePresence mode="wait">
        <motion.div
          key={showBack ? 'back' : 'front'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ width: '100%', height: '100%' }}
        >
          <Picture
            src={showBack ? back : front}
            widths={[400, 800]}
            sizes="(max-width: 768px) 92vw, 40vw"
            alt={`${alt} ${showBack ? 'back' : 'front'}`}
            wrapperStyle={{ display: 'block', width: '100%', height: '100%' }}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </motion.div>
      </AnimatePresence>
      <span style={{
        position: 'absolute', bottom: 8, right: 10,
        fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.22em',
        color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
        pointerEvents: 'none',
      }}>
        {showBack ? 'back' : 'front'}
      </span>
    </motion.div>
  );
}

function SizeChip({ size, active, onClick }) {
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.94 }}
      transition={{ duration: 0.15 }}
      aria-pressed={active}
      aria-label={`Size ${size}`}
      style={{
        flex: 1, minWidth: 36, padding: '9px 0',
        border: `1px solid ${active ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.14)'}`,
        background: active ? '#fff' : 'transparent',
        color: active ? '#000' : 'rgba(255,255,255,0.7)',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.1em',
        transition: 'all 0.15s', cursor: 'none',
      }}
    >
      {size}
    </motion.button>
  );
}

function ProductCard({ num, name, nameItalic, price, edition, sizes, children, feat = false, preorder = null, addLabel }) {
  const [size, setSize] = useState(sizes?.[0] || null);
  const { add } = useCart();
  const [toastVisible, setToastVisible] = useState(false);

  const handleAdd = () => {
    add({ id: `${num}-${size || 'default'}`, name, size, price });
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 1800);
  };

  return (
    <div style={{
      position: 'relative', display: 'flex', flexDirection: 'column',
      border: '1px solid rgba(255,255,255,0.1)',
      background: 'linear-gradient(160deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.2) 100%)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      overflow: 'hidden',
      ...(feat ? { gridRow: 'span 2' } : {}),
    }}>
      {/* Topline */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, padding: '14px 18px', whiteSpace: 'nowrap',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
        textTransform: 'uppercase',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.6)' }}>N° {num} / 04</span>
        {preorder
          ? <span style={{ color: 'var(--faint)' }}>Pre-order {preorder}</span>
          : <span style={{ color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{edition}</span>
        }
      </div>

      {/* Visual stage */}
      <div style={{
        flex: 1, minHeight: feat ? 320 : 160, position: 'relative', overflow: 'hidden',
        background: 'radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.04) 0%, transparent 70%), #060610',
        display: 'grid', placeItems: 'center', padding: 18,
      }}>
        {children}
      </div>

      {/* Footer */}
      <div style={{
        padding: 18, borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <span style={{ fontFamily: 'var(--display)', fontWeight: 300, fontSize: feat ? 36 : 22, lineHeight: 0.95 }}>{name} </span>
            {nameItalic && <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 300, fontSize: feat ? 28 : 18, color: 'rgba(255,255,255,0.7)' }}>{nameItalic}</span>}
          </div>
          <span style={{ fontFamily: 'var(--display)', fontWeight: 400, fontSize: feat ? 28 : 20, letterSpacing: '0.04em' }}>{price}</span>
        </div>

        {sizes && (
          <div style={{ display: 'flex', gap: 6 }}>
            {sizes.map(s => <SizeChip key={s} size={s} active={size === s} onClick={() => setSize(s)} />)}
          </div>
        )}

        <motion.button
          onClick={handleAdd}
          whileHover={{ background: '#fff', color: '#000' }}
          transition={{ duration: 0.2 }}
          whileTap={{ scale: 0.97 }}
          aria-label={`${addLabel} — ${name}`}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 16px', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.4)',
            color: '#fff', fontFamily: 'var(--mono)', fontSize: 11,
            letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'none',
          }}
        >
          {addLabel}
          <motion.span whileHover={{ x: 4 }} transition={{ duration: 0.2 }} aria-hidden="true">→</motion.span>
        </motion.button>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toastVisible && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute', bottom: 80, right: 16, padding: '10px 14px',
              background: 'rgba(8,8,18,0.96)', border: '1px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(12px)',
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em', color: '#fff', zIndex: 20,
            }}
          >
            +1 {name}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Shop() {
  const [selectedSticker, setSelectedSticker] = useState('bordeaux');
  const { lang } = useLang();
  const t = T[lang]?.shop || T.fr.shop;

  return (
    <section style={{ position: 'relative', minHeight: '100vh', background: 'var(--bg)', padding: '80px 56px 100px' }}>
      <div className="noise" aria-hidden="true" />

      {/* Chapter bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 18, borderBottom: '1px solid var(--line)', marginBottom: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 300 }}>V.</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}>Sky Shop</span>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.18em', color: 'var(--faint)', textTransform: 'uppercase' }}>
          First drop · Ships from Bxl · Édition limitée
        </span>
      </motion.div>

      {/* Shop header */}
      <motion.div
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
        viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
        style={{
          display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56,
          marginBottom: 32, paddingBottom: 28, borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
        className="shop-header-grid"
      >
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 200, fontSize: 'clamp(56px,8vw,120px)', lineHeight: 0.86, letterSpacing: '-0.02em', margin: 0 }}>
          Wear<br />a piece of<br />
          <em style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 300 }}>sky.</em>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 16 }}>
          <div style={{ width: 36, height: 1, background: '#fff', opacity: 0.4 }} aria-hidden="true" />
          <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 17, lineHeight: 1.55, color: 'rgba(255,255,255,0.75)', maxWidth: 420, margin: 0 }}>
            {t.intro}
          </p>
        </div>
      </motion.div>

      {/* Products grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="shop-products-grid"
        style={{ display: 'grid', gridTemplateColumns: '1.45fr 1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 18 }}
      >
        {/* N°01 Tee — featured (row span 2) */}
        <ProductCard num="01" name="Oversized Tee" nameItalic={'"Voidy Inside"'} price="€42" edition="Edition of 50" sizes={['S','M','L','XL','XXL']} feat addLabel={t.add}>
          <ProductVisual front="/shop-tee-front.png" back="/shop-tee-back.png" alt="Oversized Tee" />
        </ProductCard>

        {/* N°02 Hoodie */}
        <ProductCard num="02" name="Heavy Hoodie" price="€78" edition="Edition of 30" sizes={['S','M','L','XL']} preorder="06·26" addLabel={t.add}>
          <ProductVisual front="/shop-hoodie-front.png" back="/shop-hoodie-back.png" alt="Heavy Hoodie" />
        </ProductCard>

        {/* N°03 Chrome Stickers — color swatches in lieu of images */}
        <ProductCard num="03" name="Chrome Stickers" price="€8" edition="Pack of 4" addLabel={t.add}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 6, width: '100%', height: '100%', padding: 8 }}>
            {STICKERS.map(s => {
              const active = selectedSticker === s.key;
              return (
                <motion.div
                  key={s.key}
                  onClick={() => setSelectedSticker(s.key)}
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 0.15 }}
                  role="button"
                  tabIndex={0}
                  aria-label={s.label}
                  aria-pressed={active}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSelectedSticker(s.key)}
                  style={{
                    position: 'relative', display: 'grid', placeItems: 'center',
                    padding: 8, overflow: 'hidden',
                    background: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${active ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.08)'}`,
                    cursor: 'none',
                  }}
                >
                  <Picture
                    src={s.src}
                    widths={[128, 256]}
                    sizes="(max-width: 768px) 22vw, 110px"
                    alt={s.label}
                    wrapperStyle={{ display: 'block', width: '100%', lineHeight: 0 }}
                    style={{ width: '100%', height: 'auto', maxHeight: 72, objectFit: 'contain' }}
                  />
                </motion.div>
              );
            })}
          </div>
        </ProductCard>

        {/* N°04 Keychain */}
        <ProductCard num="04" name="Keychain Tag" price="€12" edition="Edition of 100" addLabel={t.add}>
          <VisualSlot tag="TAG" />
        </ProductCard>
      </motion.div>
    </section>
  );
}
